import { useState, useEffect } from 'react';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { Icon, Tag } from '@pypestream/design-system';
import { checkSolutionName, type SolutionNameValidity } from '../utils';

const emptyValidity: SolutionNameValidity = {
  valid: false,
  passedLengthCheck: false,
  passedUniquenessCheck: false,
};

export const useSolutionNameValidation = (watchedSolutionName: string) => {
  const { solutions } = useProStudioCtxSelector((ctx) => ({
    solutions: (ctx.solutions || [])
      .map((solution) => solution.displayName)
      .filter((name): name is string => Boolean(name)),
  }));

  const [solutionDisplayedName, setSolutionDisplayedName] = useState('');

  const [solutionNameValidityState, setSolutionNameValidityState] =
    useState<SolutionNameValidity>(emptyValidity);

  useEffect(() => {
    if (watchedSolutionName !== solutionDisplayedName) {
      setSolutionDisplayedName(watchedSolutionName || '');
      setSolutionNameValidityState(
        checkSolutionName(watchedSolutionName || '', solutions)
      );
    }
  }, [watchedSolutionName, solutionDisplayedName, solutions]);

  // No character filter and no auto-capitalization (PSTUDIO-3591): the name is
  // returned verbatim; we only recompute validity (length + uniqueness).
  const validateSolutionName = (typedName: string) => {
    setSolutionDisplayedName(typedName);
    setSolutionNameValidityState(checkSolutionName(typedName, solutions));

    return typedName;
  };

  const isValidName = solutionNameValidityState.valid;

  // Surface the length error only once the user has typed something, so an
  // untouched/empty field isn't shown as an error.
  const hasLengthError =
    !solutionNameValidityState.passedLengthCheck &&
    watchedSolutionName.trim().length > 0;

  const getTag = (
    checkLabel: string,
    checkKey: keyof Omit<SolutionNameValidity, 'valid'>
  ) => {
    const isNameEntered = solutionDisplayedName.length > 0;
    const isPassing = solutionNameValidityState[checkKey] && isNameEntered;

    return (
      <Tag
        test-id={`${checkKey}-tag`}
        {...((isNameEntered && {
          variant: isPassing ? 'success' : 'error',
          minimal: true,
          size: 'small',
        }) || {
          variant: 'default',
          size: 'small',
          minimal: true,
        })}
      >
        <Icon name={isPassing ? 'check' : 'close'} />
        {checkLabel}
      </Tag>
    );
  };

  return {
    isValidName,
    hasLengthError,
    validateSolutionName,
    getTag,
  };
};
